import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Medication as SupplementIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import moment from "moment";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const SupplementListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.5),
}));

const SupplementsCard = ({ report }) => {
  if (!report) return null;

  // Get active supplements from client data
  const activeSupplements =
    report.clientData?.supplementPrescriptions?.filter(
      (supp) => supp.isActive,
    ) || [];

  // Get supplement history from report
  const supplementHistory = report.supplementData || [];

  // If there are no supplements, don't show the card
  if (activeSupplements.length === 0 && supplementHistory.length === 0) {
    return null;
  }

  return (
    <StyledCard>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SupplementIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Supplements
            </Typography>
          </Stack>
        }
      />
      <CardContentWrapper>
        {/* Active Supplements Section */}
        <Box>
          <Typography variant="subtitle1" fontWeight="medium">
            Current Supplements
          </Typography>

          {activeSupplements.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, mb: 2 }}
            >
              No active supplements prescribed
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
              {activeSupplements.map((supplement) => (
                <Grid item xs={12} md={6} key={supplement.id}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {supplement.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {supplement.dosage}, {supplement.frequency}
                    </Typography>
                    {supplement.instructions && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5, fontStyle: "italic" }}
                      >
                        Instructions: {supplement.instructions}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {supplementHistory.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Supplement History */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                sx={{ mb: 1.5 }}
              >
                Supplement History
              </Typography>

              <List disablePadding>
                {supplementHistory.map((record) => {
                  const recordDate = new Date(record.createdOn.seconds * 1000);

                  return (
                    <SupplementListItem key={record.id}>
                      <Box sx={{ width: "100%" }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {moment(recordDate).format("MMM D, YYYY")}
                        </Typography>
                        <Stack spacing={1}>
                          {record.history.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box>
                                <Typography variant="body2">
                                  {item.supplementName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {item.dosage}, {item.frequency}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"
                                color={item.taken ? "success" : "error"}
                                icon={
                                  item.taken ? <CheckIcon /> : <CloseIcon />
                                }
                                label={item.taken ? "Taken" : "Not taken"}
                                variant="outlined"
                              />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </SupplementListItem>
                  );
                })}
              </List>
            </Box>
          </>
        )}
      </CardContentWrapper>
    </StyledCard>
  );
};

export default SupplementsCard;
